import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { ReportedPaymentWarning } from '@/components/InvoiceDetailsModal/AmendInvoiceModal';
import {
  type CancelOutcome,
  cancelOutcome,
  moneyOnOtherProformas,
} from '@/components/InvoiceDetailsModal/changeActions';
import { ApiError } from '@/lib/api';
import {
  type InvoiceDetails,
  type TReportedPayment,
  useAmendOptions,
  useCancelInvoice,
  useInvoiceDetailsByOfferId,
} from '@/models/invoices';
import type { TInvoice } from '@/types';

const OUTCOME_TEXT: Record<CancelOutcome, { color: string; text: string }> = {
  'awaits-new-proforma': {
    color: 'blue',
    text: 'The offer stays accepted and shows "Accepted - no active proforma"; its owner is notified. The owner then issues a new proforma on the accepted positions and selection at today\'s exchange rate, unlocks the offer so the client accepts again, or archives it. The client is not notified.',
  },
  'returns-first-installment': {
    color: 'blue',
    text: "The offer stays accepted and its payment schedule stays in force. Installment 1 returns to the schedule as not issued and the offer's owner is notified: they issue it again with Issue new proforma on the offer (a new number, the same installment, today's exchange rate), unlock the offer so the client accepts again, or archive it. The client is not notified.",
  },
  'offer-stays': {
    color: 'gray',
    text: 'Another proforma of this offer is still live, so the offer stays accepted.',
  },
  'returns-to-schedule': {
    color: 'orange',
    text: "The offer stays accepted. This installment returns to the payment schedule as not issued and the offer's owner is notified. Accounting can issue it again from this invoice, and the owner from the offer: a new proforma with a new number and the same installment number, at today's exchange rate, and the schedule continues. To change the paying party, currency or issuer instead, use Amend invoice.",
  },
  'cancels-renewal': {
    color: 'orange',
    text: 'This is a renewal invoice. Its renewal is cancelled with it, exactly as Cancel on the renewals screen does, so no next renewal cycle follows from it. The offer is not changed.',
  },
  'offer-untouched': {
    color: 'gray',
    text: 'The offer is not changed.',
  },
};

const storedReports = (invoice: TInvoice): TReportedPayment[] =>
  ((invoice as InvoiceDetails).paymentReports ?? [])
    .filter((r) => r.removedAt == null)
    .map((r) => ({
      id: r.id,
      source: r.source,
      hash: r.hash,
      fileId: r.fileId,
      fileName: r.fileName,
      reportedAt: r.reportedAt,
    }));

export const refusal = (error: unknown) => {
  if (!(error instanceof ApiError)) return null;
  try {
    return (JSON.parse(error.body) as { error?: string }).error ?? error.body;
  } catch {
    return error.body || null;
  }
};

export function CancelInvoiceModal({
  invoice,
  ready,
  loadFailed,
  opened,
  onClose,
}: {
  invoice: TInvoice;
  ready: boolean;
  loadFailed: boolean;
  opened: boolean;
  onClose: () => void;
}) {
  const cancel = useCancelInvoice();
  const [error, setError] = useState<string | null>(null);
  const siblings = useInvoiceDetailsByOfferId(invoice.offerId ?? 0);
  const options = useAmendOptions(invoice.id, undefined, opened);
  const outcome =
    invoice.offerId == null
      ? cancelOutcome(invoice, [])
      : siblings.data && cancelOutcome(invoice, siblings.data);
  const loading = !ready || options.isLoading || (!outcome && !siblings.error);
  const moneyElsewhere =
    !!siblings.data && moneyOnOtherProformas(invoice, siblings.data);
  const reports =
    options.data?.reportedPayments ??
    (options.isError ? storedReports(invoice) : []);

  const close = () => {
    if (cancel.isPending) return;
    setError(null);
    onClose();
  };

  const submit = async () => {
    setError(null);
    try {
      const res = await cancel.mutateAsync({
        id: invoice.id,
        offerId: invoice.offerId,
      });
      setError(null);
      onClose();
      notifications.show({
        color: 'green',
        title: `${invoice.invoiceNumber} cancelled`,
        message: res.noActiveProforma
          ? 'The offer stays accepted with no active proforma; its owner was notified.'
          : 'The offer stays accepted.',
      });
    } catch (e) {
      setError(
        refusal(e) ??
          'We did not get a response. Check the invoice status before trying again.',
      );
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={`Cancel ${invoice.invoiceNumber}`}
      size="md"
      centered
      closeOnClickOutside={!cancel.isPending}
      closeOnEscape={!cancel.isPending}
      withCloseButton={!cancel.isPending}
    >
      <Stack gap="md">
        <Text size="sm">
          A cancelled proforma can no longer be paid, amended or sent, and its
          number is never reused. This cannot be undone.
        </Text>

        {reports.length > 0 && (
          <ReportedPaymentWarning
            reports={reports}
            invoiceNumber={invoice.invoiceNumber}
            consequence="Check them before cancelling: the payer may have paid against this document."
          />
        )}

        {loadFailed ? (
          <Alert color="orange">
            <Text size="sm">
              This invoice could not be loaded, so it cannot be cancelled from
              here. Close this and try again.
            </Text>
          </Alert>
        ) : loading ? (
          <Group justify="center">
            <Loader size="sm" />
          </Group>
        ) : outcome ? (
          <Alert color={OUTCOME_TEXT[outcome].color}>
            <Text size="sm">{OUTCOME_TEXT[outcome].text}</Text>
            {moneyElsewhere && (
              <Text size="sm" mt={4}>
                Money is recorded on another proforma of this offer, so the
                offer cannot be unlocked.
              </Text>
            )}
          </Alert>
        ) : (
          <Alert color="orange">
            <Text size="sm">
              The other proformas of this offer could not be loaded, so what
              happens to the offer cannot be shown. Close this and try again.
            </Text>
          </Alert>
        )}

        {error && (
          <Alert color="red" title="Not cancelled">
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={close} disabled={cancel.isPending}>
            Keep invoice
          </Button>
          <Button
            color="red"
            onClick={submit}
            loading={cancel.isPending}
            disabled={loadFailed || loading || !outcome}
          >
            Cancel invoice
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
